"use client";

import React from "react";
import Link from "next/link";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-extrabold text-zinc-900 dark:text-zinc-100 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-zinc dark:prose-invert max-w-none">
          <p className="lead">
            Last updated: June 3, 2025
          </p>
          
          <h2>1. Introduction</h2>
          <p>
            At Kled.io ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our remote environment ecosystem services.
          </p>
          <p>
            Please read this Privacy Policy carefully. By accessing or using our services, you acknowledge that you have read, understood, and agree to be bound by all the terms outlined in this policy.
          </p>
          
          <h2>2. Information We Collect</h2>
          <p>
            We may collect information about you in various ways:
          </p>
          <h3>2.1 Personal Information You Provide</h3>
          <ul>
            <li>Account information: When you register, we collect your name, email address, company name, and credentials.</li>
            <li>Profile information: Information you provide in your user profile.</li>
            <li>Payment information: Billing details and transaction history.</li>
            <li>Communications: Information provided when contacting us for support or inquiries.</li>
          </ul>
          
          <h3>2.2 Information Automatically Collected</h3>
          <ul>
            <li>Usage data: Information about how you interact with our services, features you use, and actions you take.</li>
            <li>Device information: IP address, browser type, operating system, and device information.</li>
            <li>Log data: Server logs, error reports, and performance data.</li>
            <li>Cookies and similar technologies: Information collected through cookies and similar tracking technologies.</li>
          </ul>
          
          <h3>2.3 Information From Third Parties</h3>
          <p>
            We may receive information about you from third-party partners, service providers, and publicly available sources.
          </p>
          
          <h2>3. How We Use Your Information</h2>
          <p>
            We may use the information we collect for various purposes, including:
          </p>
          <ul>
            <li>Providing, maintaining, and improving our services</li>
            <li>Processing transactions and managing your account</li>
            <li>Communicating with you about our services, updates, and support</li>
            <li>Personalizing your experience and providing tailored content</li>
            <li>Analyzing usage patterns to improve our services</li>
            <li>Protecting our services and preventing fraudulent activity</li>
            <li>Complying with legal obligations</li>
          </ul>
          
          <h2>4. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security assessments and audits</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Staff training on data protection practices</li>
          </ul>
          <p>
            While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
          </p>
          
          <h2>5. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When determining the retention period, we consider:
          </p>
          <ul>
            <li>The amount, nature, and sensitivity of the information</li>
            <li>The potential risk of harm from unauthorized use or disclosure</li>
            <li>The purposes for which we process the information</li>
            <li>Legal and regulatory requirements</li>
          </ul>
          
          <h2>6. Data Sharing and Disclosure</h2>
          <p>
            We may share your information in the following circumstances:
          </p>
          <ul>
            <li>With service providers who perform services on our behalf</li>
            <li>With business partners, with your consent</li>
            <li>In connection with a business transaction (e.g., merger, acquisition, or sale)</li>
            <li>When required by law or to protect our rights</li>
            <li>With your consent or at your direction</li>
          </ul>
          
          <h2>7. Your Privacy Rights</h2>
          <p>
            Depending on your location, you may have certain rights regarding your personal information, including:
          </p>
          <ul>
            <li>Right to access: Request access to your personal information</li>
            <li>Right to rectification: Request correction of inaccurate information</li>
            <li>Right to erasure: Request deletion of your information under certain circumstances</li>
            <li>Right to restrict processing: Request limitation of how we use your information</li>
            <li>Right to data portability: Request transfer of your information</li>
            <li>Right to object: Object to certain types of processing</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information provided in the "Contact Us" section.
          </p>
          
          <h2>8. International Data Transfers</h2>
          <p>
            We are based in Australia and process data both locally and in other countries. When we transfer personal information outside of Australia, we implement appropriate safeguards to ensure that your information receives adequate protection.
          </p>
          
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us.
          </p>
          
          <h2>10. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website or through other communication channels.
          </p>
          
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
          </p>
          <p>
            Kled.io<br />
            Email: privacy@kled.io<br />
            Address: 101 Technology Drive, Sydney, NSW 2000, Australia
          </p>
        </div>
        
        <div className="mt-12 flex justify-center">
          <Link 
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
